import { showDeleteConfirmation } from '@modules/common';
import { map, switchMap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { ProposalTemplateFolderId, ProposalTemplateFolderResponse } from '@business/entities/proposal';
import { ProposalTemplateFolderAPI } from '@business/api/proposal_template_folder_api';

export class ProposalTemplateFolderBloc extends BaseBloc {
  private readonly foldersRepo = new Repository<ProposalTemplateFolderResponse>({
    getItemId: folder => folder.id,
  });

  readonly folders$ = this.foldersRepo.items$;
  readonly selectFolder = this.foldersRepo.selectItem;

  onReset() {
    this.foldersRepo.reset();
  }

  fetchFolders = (parentId?: ProposalTemplateFolderId) => {
    return ProposalTemplateFolderAPI.fetchFolders(parentId).pipe(
      this.foldersRepo.ops.upsertMany(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_proposal_template_folder') }),
    );
  };

  fetchFolder = (id: ProposalTemplateFolderId) => {
    return ProposalTemplateFolderAPI.fetchFolder(id).pipe(
      this.foldersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_proposal_template_folder') }),
    );
  };

  createFolder = (data: Partial<ProposalTemplateFolderResponse>) => {
    return ProposalTemplateFolderAPI.createFolder(data).pipe(
      this.foldersRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_proposal_template_folder'),
      }),
    );
  };

  updateFolder = (id: ProposalTemplateFolderId, data: Partial<ProposalTemplateFolderResponse>) => {
    return ProposalTemplateFolderAPI.updateFolder(id, data).pipe(
      this.foldersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_proposal_template_folder'),
      }),
    );
  };

  deleteFolder = (id: ProposalTemplateFolderId) => {
    return showDeleteConfirmation('Delete Folder', `Do you really want to remove this folder?`).pipe(
      switchMap(() => ProposalTemplateFolderAPI.deleteFolder(id)),
      this.foldersRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_proposal_template_folder'),
      }),
    );
  };
}
